// EWM-809 — client-side queryID attribution store (hop 1: click → PDP → convert).
// Mirrors what Algolia's InstantSearch insights middleware does internally:
// remember the queryID that surfaced a product so a later PDP conversion can be
// tied back to the originating search. Persisted per-sku with a TTL matching
// Algolia's default click-attribution window.

import { hasAnalyticsConsent } from '@/lib/analytics/consent';

const KEY_PREFIX = 'algolia_qid:';
export const QUERY_ID_TTL_MS = 30 * 60 * 1000; // 30 min

interface StoredEntry {
  queryID: string;
  index: string;
  ts: number;
}

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

// Drop every expired `algolia_qid:` key. recallQueryId only evicts the key it's
// asked for, so a shopper who clicks many results without revisiting them would
// otherwise leave keys past their TTL indefinitely. Best-effort housekeeping run
// on each write; never throws.
const sweepExpired = (now: number): void => {
  try {
    const stale: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(KEY_PREFIX)) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      try {
        const { ts } = JSON.parse(raw) as StoredEntry;
        if (typeof ts !== 'number' || now - ts > QUERY_ID_TTL_MS) stale.push(key);
      } catch {
        stale.push(key); // unparseable → drop
      }
    }
    stale.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* ignore — housekeeping is best-effort */
  }
};

export const rememberQueryId = (sku: string, queryID: string, index: string): void => {
  if (!isBrowser() || !sku || !queryID) return;
  // EWM-809 — don't persist an analytics identifier without consent (parity with
  // the Insights event helpers, which are all consent-gated).
  if (!hasAnalyticsConsent()) return;
  const now = Date.now();
  sweepExpired(now);
  const entry: StoredEntry = { queryID, index, ts: now };
  try {
    window.localStorage.setItem(`${KEY_PREFIX}${sku}`, JSON.stringify(entry));
  } catch {
    // quota exceeded / private mode — attribution is best-effort, never fatal.
  }
};

export const recallQueryId = (sku: string): { queryID: string; index: string } | undefined => {
  if (!isBrowser() || !sku) return undefined;
  const raw = (() => {
    try {
      return window.localStorage.getItem(`${KEY_PREFIX}${sku}`);
    } catch {
      return null;
    }
  })();
  if (!raw) return undefined;
  let entry: StoredEntry;
  try {
    entry = JSON.parse(raw) as StoredEntry;
  } catch {
    return undefined;
  }
  if (!entry.queryID || Date.now() - entry.ts > QUERY_ID_TTL_MS) {
    try {
      window.localStorage.removeItem(`${KEY_PREFIX}${sku}`);
    } catch {
      /* ignore */
    }
    return undefined;
  }
  return { queryID: entry.queryID, index: entry.index };
};
